import { useEffect, useMemo, useRef, useState } from 'react'
import type { InfiniteData } from '@tanstack/react-query'
import { ApiError } from '@/api/client'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { LoadingScreen } from '@/components/LoadingScreen'
import {
  type DirectoryUser,
  type FavoriteTeam,
  type FriendsPaginatedResponse,
  type FriendshipActionKind,
  type FriendshipStatus,
  type FriendshipSummaryResponse,
  type PaginatedResponse,
  type UserProfile,
  getDisplayName,
  getFavoriteTeamName,
  useDirectoryUsers,
  useFriendshipAction,
  useFriendshipSummary,
  useFriendsList,
  useReportUser,
  useSearchUsers,
  useUserProfile,
} from '@/features/friends/api'
import { useCurrentUser } from '@/features/user/api'
import { useTranslation } from '@/i18n'

type FriendsSegment = 'friends' | 'discover'

type ActiveUsersQuery = {
  data?: InfiniteData<PaginatedResponse<DirectoryUser> | FriendsPaginatedResponse> | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  isFetchingNextPage: boolean
  hasNextPage?: boolean
  fetchNextPage: () => Promise<unknown>
  refetch: () => Promise<unknown>
}

export function FriendsPage() {
  const { t } = useTranslation()
  const [segment, setSegment] = useState<FriendsSegment>('friends')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const friendsQuery = useFriendsList(debouncedSearchQuery.length === 0)
  const directoryQuery = useDirectoryUsers(segment === 'discover' && debouncedSearchQuery.length === 0)
  const searchQueryResult = useSearchUsers(
    debouncedSearchQuery,
    debouncedSearchQuery.length > 0,
  )
  const summaryQuery = useFriendshipSummary(true)
  const friendshipAction = useFriendshipAction()
  const reportUser = useReportUser()
  const currentUserQuery = useCurrentUser(true)
  const profileQuery = useUserProfile(selectedUserId, selectedUserId != null)
  const isSearchActive = debouncedSearchQuery.trim().length > 0

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim())
    }, 350)

    return () => window.clearTimeout(timer)
  }, [searchQuery])

  const activeQuery: ActiveUsersQuery = isSearchActive
    ? searchQueryResult
    : segment === 'friends'
      ? friendsQuery
      : directoryQuery

  const activeUsers = useMemo(() => {
    const items = (activeQuery.data?.pages ?? []).flatMap((page) => page.data)
    return isSearchActive || segment === 'discover' ? items : sortFriends(items)
  }, [activeQuery.data?.pages, isSearchActive, segment])

  const fallbackSummary = useMemo(
    () => buildFallbackSummary(friendsQuery.data),
    [friendsQuery.data],
  )
  const summary = summaryQuery.data?.data ?? fallbackSummary

  const isPageLoading =
    currentUserQuery.isLoading ||
    (segment === 'friends' && !isSearchActive && friendsQuery.isLoading) ||
    (segment === 'discover' && !isSearchActive && directoryQuery.isLoading) ||
    (isSearchActive && searchQueryResult.isLoading)

  if (currentUserQuery.isLoading && !currentUserQuery.data) return <LoadingScreen />

  return (
    <>
      <div className="space-y-6">
        <section className="rounded-[1.5rem] border border-white/[0.06] bg-white/[0.02] p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-accent-400">
                {t('Vrienden')}
              </p>
              <h1 className="mt-3 text-2xl font-black tracking-tight text-white">
                {t('Bouw je netwerk in Golero')}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-surface-400">
                {t('Beheer je vrienden, ontdek andere spelers en stuur direct friend requests vanuit dezelfde flow als in de app.')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-1.5 rounded-[0.95rem] bg-white/[0.04] p-1">
              <SegmentButton active={segment === 'friends'} onClick={() => setSegment('friends')}>
                {t('Vrienden')}
              </SegmentButton>
              <SegmentButton active={segment === 'discover'} onClick={() => setSegment('discover')}>
                {t('Ontdekken')}
              </SegmentButton>
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-[1rem] bg-white/[0.03] p-4">
              <label htmlFor="friends-search" className="sr-only">
                {t('Zoek spelers')}
              </label>
              <input
                id="friends-search"
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t('Zoek spelers, vrienden of uitnodigingen')}
                className="w-full rounded-[0.9rem] border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-surface-500 focus:border-accent-500 focus:outline-none"
              />
              <p className="mt-2 text-xs text-surface-500">
                {isSearchActive
                  ? t('Zoekresultaten voor "{query}".', { query: debouncedSearchQuery })
                  : t('Zoeken gebruikt de directory en vervangt tijdelijk de actieve lijst.')}
              </p>
            </div>

            <section className="grid gap-3 sm:grid-cols-3">
              <SummaryCard label={t('Vrienden')} value={String(summary.friends_count)} />
              <SummaryCard label={t('Inkomend')} value={String(summary.pending_incoming_count)} />
              <SummaryCard label={t('Uitgaand')} value={String(summary.pending_outgoing_count)} />
            </section>
          </div>
        </section>

        {isPageLoading ? (
          <InfoPanel
            title={t('Vrienden laden')}
            body={t('We halen je vrienden, uitnodigingen en directory op.')}
          />
        ) : (
          <FriendsListSection
            title={getSectionTitle(t, segment, isSearchActive)}
            subtitle={getSectionSubtitle(t, segment, isSearchActive)}
            query={activeQuery}
            users={activeUsers}
            isSearchActive={isSearchActive}
            onOpenProfile={(userId) => setSelectedUserId(userId)}
            onRetry={() => void activeQuery.refetch()}
            onAction={(user, action) =>
              friendshipAction.mutate({
                userId: user.id,
                action,
                currentStatus: user.friendship_status,
              })
            }
            actionPendingUserId={friendshipAction.variables?.userId}
            actionPendingKind={friendshipAction.variables?.action}
            actionError={friendshipAction.error?.message}
          />
        )}
      </div>

      {selectedUserId != null ? (
        <UserProfileSheet
          userId={selectedUserId}
          currentUserId={currentUserQuery.data?.id}
          query={profileQuery}
          onClose={() => {
            if (friendshipAction.isPending || reportUser.isPending) return
            setSelectedUserId(null)
          }}
          onAction={(profile, action) =>
            friendshipAction.mutate({
              userId: profile.id,
              action,
              currentStatus: profile.friendship_status,
            })
          }
          onReport={(userId) => reportUser.mutate({ userId })}
          friendshipPending={friendshipAction.isPending && friendshipAction.variables?.userId === selectedUserId}
          friendshipError={friendshipAction.error?.message}
          reportPending={reportUser.isPending && reportUser.variables?.userId === selectedUserId}
          reportError={reportUser.error instanceof Error ? reportUser.error.message : undefined}
          reportSuccessMessage={reportUser.data?.message}
        />
      ) : null}
    </>
  )
}

function FriendsListSection({
  title,
  subtitle,
  query,
  users,
  isSearchActive,
  onOpenProfile,
  onRetry,
  onAction,
  actionPendingUserId,
  actionPendingKind,
  actionError,
}: {
  title: string
  subtitle: string
  query: ActiveUsersQuery
  users: DirectoryUser[]
  isSearchActive: boolean
  onOpenProfile: (userId: number) => void
  onRetry: () => void
  onAction: (user: DirectoryUser, action: FriendshipActionKind) => void
  actionPendingUserId?: number | undefined
  actionPendingKind?: FriendshipActionKind | undefined
  actionError?: string | undefined
}) {
  const { t } = useTranslation()
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = sentinelRef.current
    if (!node || query.hasNextPage !== true || query.isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void query.fetchNextPage()
        }
      },
      { rootMargin: '260px 0px' },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [query])

  if (query.isError) {
    return (
      <ErrorPanel
        title={t('Vrienden niet geladen')}
        body={query.error?.message || t('De vriendenpagina kon niet worden opgebouwd.')}
        cta={t('Opnieuw proberen')}
        onClick={onRetry}
      />
    )
  }

  if (users.length === 0) {
    return (
      <InfoPanel
        title={isSearchActive ? t('Geen zoekresultaten') : t('Nog niets gevonden')}
        body={
          isSearchActive
            ? t('Er zijn geen spelers gevonden voor deze zoekopdracht.')
            : t('Je hebt nog geen vrienden of er zijn nog geen spelers beschikbaar in deze lijst.')
        }
      />
    )
  }

  return (
    <section className="rounded-[1.5rem] border border-white/[0.06] bg-white/[0.02] p-4 sm:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
            {title}
          </p>
          <h2 className="mt-2 text-xl font-black text-white">{subtitle}</h2>
        </div>
        <span className="rounded-full bg-white/[0.04] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-surface-400">
          {t('{count} zichtbaar', { count: users.length })}
        </span>
      </div>

      {actionError ? (
        <p className="mt-4 rounded-[0.95rem] bg-red-950/35 px-3.5 py-2.5 text-sm text-red-200">
          {actionError}
        </p>
      ) : null}

      <div className="mt-4 space-y-3">
        {users.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            onOpenProfile={() => onOpenProfile(user.id)}
            onAction={(action) => onAction(user, action)}
            isPending={actionPendingUserId === user.id}
            pendingAction={actionPendingKind}
          />
        ))}
      </div>

      <div ref={sentinelRef} />

      {query.hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="rounded-full bg-white/[0.04] px-4 py-2 text-sm font-semibold text-surface-200 transition hover:bg-white/[0.07] disabled:opacity-50"
          >
            {query.isFetchingNextPage ? t('Meer laden…') : t('Meer laden')}
          </button>
        </div>
      ) : null}
    </section>
  )
}

function UserCard({
  user,
  onOpenProfile,
  onAction,
  isPending,
  pendingAction,
}: {
  user: DirectoryUser
  onOpenProfile: () => void
  onAction: (action: FriendshipActionKind) => void
  isPending: boolean
  pendingAction?: FriendshipActionKind | undefined
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onOpenProfile}
      className="flex w-full items-center gap-3 rounded-[1rem] bg-white/[0.03] px-3.5 py-3 text-left transition hover:bg-white/[0.06]"
    >
      <Avatar name={getDisplayName(user)} image={user.profile_image} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold text-white">{getDisplayName(user)}</p>
          <RankPill rankScore={user.rank_score} rankLabel={user.rank?.label} />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-surface-400">
          {user.favorite_team ? <span>{getFavoriteTeamName(user.favorite_team)}</span> : null}
          {user.favorite_team && user.stats?.poules_count ? (
            <span aria-hidden="true" className="h-1 w-1 rounded-full bg-surface-700" />
          ) : null}
          {typeof user.stats?.poules_count === 'number' ? (
            <span>{user.stats.poules_count} {t('poules')}</span>
          ) : null}
        </div>
      </div>
      <div
        className="shrink-0"
        onClick={(event) => event.stopPropagation()}
      >
        <FriendshipActions
          status={user.friendship_status}
          isPending={isPending}
          pendingAction={pendingAction}
          onAction={onAction}
        />
      </div>
    </button>
  )
}

function UserProfileSheet({
  userId,
  currentUserId,
  query,
  onClose,
  onAction,
  onReport,
  friendshipPending,
  friendshipError,
  reportPending,
  reportError,
  reportSuccessMessage,
}: {
  userId: number
  currentUserId?: number | undefined
  query: ReturnType<typeof useUserProfile>
  onClose: () => void
  onAction: (profile: UserProfile, action: FriendshipActionKind) => void
  onReport: (userId: number) => void
  friendshipPending: boolean
  friendshipError?: string | undefined
  reportPending: boolean
  reportError?: string | undefined
  reportSuccessMessage?: string | undefined
}) {
  const { t } = useTranslation()
  const [reportedThisSession, setReportedThisSession] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.sessionStorage.getItem(getReportSessionKey(userId)) === '1'
  })

  useEffect(() => {
    if (!reportSuccessMessage || typeof window === 'undefined') return
    window.sessionStorage.setItem(getReportSessionKey(userId), '1')
    setReportedThisSession(true)
  }, [reportSuccessMessage, userId])

  return (
    <div className="animate-drawer-backdrop-in fixed inset-0 z-[72] bg-black/88 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[82vh] w-full items-stretch px-3 pb-3 sm:px-4">
        <div className="animate-manager-player-picker-in relative mx-auto flex w-full max-w-3xl flex-col overflow-hidden rounded-[1.4rem] border border-white/[0.08] bg-[#05080d] shadow-[0_18px_56px_rgba(0,0,0,0.5)]">
          <div className="border-b border-white/[0.06] px-4 pb-4 pt-3 sm:px-5">
            <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-surface-700" />
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
                  {t('Profiel')}
                </p>
                <h2 className="mt-2 text-xl font-black text-white">
                  {query.data ? getDisplayName(query.data) : `Speler ${userId}`}
                </h2>
              </div>
              <ModalCloseButton onClick={onClose} label={t('Sluiten')} />
            </div>
          </div>

          <div className="overflow-y-auto p-4 sm:p-5">
            {query.isLoading ? (
              <InfoPanel title={t('Profiel laden')} body={t('We halen extra profielinformatie op.')} />
            ) : query.isError || !query.data ? (
              <ErrorPanel
                title={t('Profiel niet geladen')}
                body={query.error?.message || t('Het profiel kon niet worden geladen.')}
                cta={t('Opnieuw proberen')}
                onClick={() => void query.refetch()}
              />
            ) : (
              <div className="space-y-5">
                <section>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <Avatar name={getDisplayName(query.data)} image={query.data.profile_image} size="lg" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-xl font-black text-white">
                          {getDisplayName(query.data)}
                        </p>
                        <RankPill rankScore={query.data.rank_score} rankLabel={query.data.rank?.label} />
                      </div>
                      {query.data.favorite_team ? (
                        <p className="mt-2 text-sm text-surface-400">
                          {t('Favoriet')}: {getFavoriteTeamName(query.data.favorite_team)}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <ProfileMetric label={t('Rank score')} value={String(query.data.rank_score)} />
                    <ProfileMetric label={t('Vrienden')} value={String(query.data.friends_count)} />
                    <ProfileMetric label={t('Poules')} value={String(query.data.poules_count)} />
                  </div>

                  {friendshipError ? (
                    <p className="mt-4 rounded-[0.95rem] bg-red-950/35 px-3.5 py-2.5 text-sm text-red-200">
                      {friendshipError}
                    </p>
                  ) : null}

                  {query.data.id !== currentUserId ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <FriendshipActions
                        status={query.data.friendship_status}
                        isPending={friendshipPending}
                        onAction={(action) => onAction(query.data, action)}
                      />
                    </div>
                  ) : null}
                </section>

                <section>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
                    {t('Poules')}
                  </p>
                  {query.data.poules.length === 0 ? (
                    <p className="mt-3 text-sm text-surface-400">{t('Nog geen gedeelde of zichtbare poules.')}</p>
                  ) : (
                    <div className="mt-3 space-y-2.5">
                      {query.data.poules.map((poule) => (
                        <div
                          key={poule.id}
                          className="flex items-center gap-3 rounded-[0.95rem] bg-white/[0.03] px-3 py-3"
                        >
                          {poule.image_url ? (
                            <img src={poule.image_url} alt="" className="h-10 w-10 rounded-lg object-cover" />
                          ) : (
                            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/[0.06] text-xs font-black uppercase text-surface-300">
                              {getInitials(poule.name)}
                            </span>
                          )}
                          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-white">
                            {poule.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {query.data.id !== currentUserId ? (
                  <section>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
                      {t('Veiligheid')}
                    </p>
                    <p className="mt-2 text-sm text-surface-400">
                      {t('Rapporteer dit profiel als de inhoud of interactie niet klopt.')}
                    </p>
                    {reportError ? (
                      <p className="mt-3 rounded-[0.95rem] bg-red-950/35 px-3.5 py-2.5 text-sm text-red-200">
                        {reportError}
                      </p>
                    ) : null}
                    {reportedThisSession && reportSuccessMessage ? (
                      <p className="mt-3 rounded-[0.95rem] bg-emerald-950/35 px-3.5 py-2.5 text-sm text-emerald-200">
                        {reportSuccessMessage}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => onReport(query.data.id)}
                      disabled={reportPending || reportedThisSession}
                      className="mt-3 rounded-full border border-red-500/25 bg-red-950/20 px-4 py-2 text-sm font-semibold text-red-100 transition hover:bg-red-950/35 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {reportedThisSession
                        ? t('Al gerapporteerd')
                        : reportPending
                          ? t('Rapporteren…')
                          : t('Gebruiker rapporteren')}
                    </button>
                  </section>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function FriendshipActions({
  status,
  isPending,
  pendingAction,
  onAction,
}: {
  status: FriendshipStatus
  isPending: boolean
  pendingAction?: FriendshipActionKind | undefined
  onAction: (action: FriendshipActionKind) => void
}) {
  const { t } = useTranslation()
  if (status === 'pending_incoming') {
    return (
      <div className="flex flex-wrap gap-2">
        <ActionButton
          tone="primary"
          disabled={isPending}
          onClick={() => onAction('accept')}
        >
          {isPending && pendingAction === 'accept' ? t('Accepteren…') : t('Accepteren')}
        </ActionButton>
        <ActionButton
          tone="muted"
          disabled={isPending}
          onClick={() => onAction('dismiss')}
        >
          {isPending && pendingAction === 'dismiss' ? t('Weigeren…') : t('Weigeren')}
        </ActionButton>
      </div>
    )
  }

  if (status === 'pending_outgoing') {
    return (
      <ActionButton tone="muted" disabled={isPending} onClick={() => onAction('revoke')}>
        {isPending ? t('Intrekken…') : t('Uitgenodigd')}
      </ActionButton>
    )
  }

  if (status === 'accepted') {
    return (
      <ActionButton tone="muted" disabled={isPending} onClick={() => onAction('revoke')}>
        {isPending ? t('Verwijderen…') : t('Verwijderen')}
      </ActionButton>
    )
  }

  return (
    <ActionButton tone="primary" disabled={isPending} onClick={() => onAction('send')}>
      {isPending ? t('Versturen…') : t('Vriend worden')}
    </ActionButton>
  )
}

function SegmentButton({
  active,
  children,
  onClick,
}: {
  active: boolean
  children: React.ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-[0.8rem] px-3.5 py-2 text-sm font-semibold transition',
        active
          ? 'bg-accent-600 text-white'
          : 'text-surface-400 hover:bg-white/[0.06] hover:text-surface-100',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function ActionButton({
  children,
  onClick,
  disabled,
  tone,
}: {
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean | undefined
  tone: 'primary' | 'muted'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'rounded-full px-3.5 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'primary'
          ? 'bg-accent-600 text-white hover:bg-accent-500'
          : 'bg-white/[0.04] text-surface-200 hover:bg-white/[0.07]',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[1rem] bg-white/[0.03] p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
        {label}
      </p>
      <p className="mt-2 text-2xl font-black text-white">{value}</p>
    </div>
  )
}

function ProfileMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[0.95rem] bg-white/[0.03] px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-surface-500">
        {label}
      </p>
      <p className="mt-2 text-lg font-black text-white">{value}</p>
    </div>
  )
}

function RankPill({
  rankScore,
  rankLabel,
}: {
  rankScore: number
  rankLabel?: string | null | undefined
}) {
  return (
    <span className="inline-flex rounded-full bg-white/[0.04] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
      {rankLabel?.trim() ? `${rankLabel} · ${rankScore}` : `${rankScore} score`}
    </span>
  )
}

function Avatar({
  name,
  image,
  size = 'md',
}: {
  name: string
  image?: string | null | undefined
  size?: 'md' | 'lg'
}) {
  const dimensions = size === 'lg' ? 'h-16 w-16 text-lg' : 'h-12 w-12 text-sm'

  if (image) {
    return <img src={image} alt="" className={`${dimensions} shrink-0 rounded-full object-cover`} />
  }

  return (
    <span
      className={`flex ${dimensions} shrink-0 items-center justify-center rounded-full bg-white/[0.06] font-black uppercase text-surface-300`}
    >
      {getInitials(name)}
    </span>
  )
}

function InfoPanel({ title, body }: { title: string; body: string }) {
  return (
    <section className="rounded-[1.5rem] border border-white/[0.06] bg-white/[0.02] p-5">
      <h2 className="text-xl font-black tracking-tight text-white">{title}</h2>
      <p className="mt-2 text-sm text-surface-400">{body}</p>
    </section>
  )
}

function ErrorPanel({
  title,
  body,
  cta,
  onClick,
}: {
  title: string
  body: string
  cta: string
  onClick: () => void
}) {
  return (
    <section className="rounded-[1.35rem] border border-red-900/60 bg-red-950/25 p-5 text-red-100">
      <h2 className="text-xl font-black tracking-tight text-white">{title}</h2>
      <p className="mt-2 text-sm text-red-100/80">{body}</p>
      <button
        type="button"
        onClick={onClick}
        className="mt-4 rounded-full border border-red-200/20 px-4 py-2 text-sm font-semibold transition hover:bg-white/5"
      >
        {cta}
      </button>
    </section>
  )
}

function buildFallbackSummary(
  data: InfiniteData<FriendsPaginatedResponse> | undefined,
): FriendshipSummaryResponse['data'] {
  const items = (data?.pages ?? []).flatMap((page) => page.data)
  const acceptedCount = items.filter((user) => user.friendship_status === 'accepted').length
  const incomingCount = items.filter((user) => user.friendship_status === 'pending_incoming').length
  const firstPage = data?.pages[0]

  return {
    friends_count: acceptedCount,
    pending_incoming_count: incomingCount,
    pending_outgoing_count: 0,
    list_count: firstPage?.meta.total ?? items.length,
  }
}

function sortFriends(users: DirectoryUser[]) {
  return [...users].sort((left, right) => {
    if (left.friendship_status === 'pending_incoming' && right.friendship_status !== 'pending_incoming') {
      return -1
    }
    if (left.friendship_status !== 'pending_incoming' && right.friendship_status === 'pending_incoming') {
      return 1
    }
    return getDisplayName(left).localeCompare(getDisplayName(right), 'nl')
  })
}

type Translate = (key: string, values?: Record<string, string | number>) => string

function getSectionTitle(t: Translate, segment: FriendsSegment, isSearchActive: boolean) {
  if (isSearchActive) return t('Zoeken')
  return segment === 'friends' ? t('Jouw netwerk') : t('Directory')
}

function getSectionSubtitle(t: Translate, segment: FriendsSegment, isSearchActive: boolean) {
  if (isSearchActive) return t('Resultaten uit de spelersdirectory')
  return segment === 'friends'
    ? t('Inkomende verzoeken bovenaan, daarna je geaccepteerde vrienden')
    : t('Ontdek andere spelers en stuur uitnodigingen')
}

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function getReportSessionKey(userId: number) {
  return `friends-report-${userId}`
}
